import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Checkbox, DialogContentText, FormControlLabel, FormGroup } from '@mui/material';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import PropTypes from 'prop-types';
import { boolean, object } from 'yup';

import { AusType } from 'constants/loanApplication';
import useForm from 'hooks/useForm';
import useLoading from 'hooks/useLoading';

import { ResetAusFindingsTestIds } from './ResetAusFindings.testIds';

const defaultObject = {};

const schema = object().shape({
  duSelected: boolean(),
  lpSelected: boolean(),
});

const ResetAusFindings = ({ formRef = null, onAccept, ausResults = defaultObject }) => {
  const [isLoading, setIsLoading] = useLoading(false);

  const isDUDisabled = !ausResults?.duStatus;
  const isLPDisabled = !ausResults?.lpStatus;

  const { form, setSchema, setInForm, validateField, validateForm } = useForm({
    duSelected: false,
    lpSelected: false,
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const handleCheckboxChange = useCallback(
    field => event => {
      setInForm(field, event.target.checked);
      validateField(field);
    },
    [setInForm, validateField],
  );

  const isFormValid = useMemo(() => {
    return form.duSelected || form.lpSelected;
  }, [form.duSelected, form.lpSelected]);

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      if (!isFormValid) {
        return;
      }

      setIsLoading(true);

      const getAusTypeToReset = () => {
        if (form.duSelected && form.lpSelected) {
          return AusType.DU_LP;
        }

        if (form.duSelected) {
          return AusType.DU;
        }

        if (form.lpSelected) {
          return AusType.LP;
        }
      };

      const ausTypeToReset = getAusTypeToReset();

      onAccept(ausTypeToReset).finally(() => setIsLoading(false));
    },
    [validateForm, setIsLoading, onAccept, form, isFormValid],
  );

  return (
    <ModalFormWrapper
      formRef={formRef}
      formTestId="loan-action-reset-aus-findings-form"
      isFormChanged={isFormValid}
      isSubmitting={isLoading}
      submitButtonName="Reset Findings"
      onSubmit={onSubmit}
    >
      <DialogContentText>
        Please select the AUS findings you would like to run as new findings.
      </DialogContentText>

      <FormGroup className="mb-24">
        <FormControlLabel
          control={
            <Checkbox
              checked={form.duSelected}
              data-testid={ResetAusFindingsTestIds.DU_INPUT_CHECKBOX}
              disabled={isDUDisabled || isLoading}
              name="duSelected"
              onChange={handleCheckboxChange('duSelected')}
            />
          }
          data-testid={ResetAusFindingsTestIds.DU_CHECKBOX}
          label="DU"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.lpSelected}
              data-testid={ResetAusFindingsTestIds.LP_INPUT_CHECKBOX}
              disabled={isLPDisabled || isLoading}
              name="lpSelected"
              onChange={handleCheckboxChange('lpSelected')}
            />
          }
          data-testid={ResetAusFindingsTestIds.LP_CHECKBOX}
          label="LP"
        />
      </FormGroup>
    </ModalFormWrapper>
  );
};

ResetAusFindings.propTypes = {
  onAccept: PropTypes.func.isRequired,
  ausResults: PropTypes.shape({
    duStatus: PropTypes.bool,
    lpStatus: PropTypes.bool,
  }),
  formRef: PropTypes.shape({ current: PropTypes.object }),
};

export default memo(ResetAusFindings);
